import classNames from 'classnames';
import * as React from 'react';
import { AdmissionCallToActionOverlayStatusChanged } from '../../AdmissionCallToAction/Events/AdmissionCallToActionOverlayStatusChanged';
import { useMessageHandler } from '~/Features/Messaging/useMessageHandler';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { assertNever } from '~/Partials/Utils/typeUtils';
import type { Theme } from '~/Utils/Theme';
import styles from './NavigationControls.module.css';

interface NavigationControlsProps {
    isOpen: boolean;
    ariaControlsId: string;
    onExpandClick: () => void;
    onApplyClick: () => void;
    applyButtonLabel: string;
    theme: Theme;
    currentItemLabel?: string;
}

export const NavigationControls: React.FC<React.PropsWithChildren<NavigationControlsProps>> = props => {
    const { isOpen, children, onApplyClick, onExpandClick, currentItemLabel, applyButtonLabel, ariaControlsId, theme } =
        props;
    const { withRoundedCorners } = theme;

    const [callToActionIsDisabled, setCallToActionIsDisabled] = React.useState(false);
    const [callToActionIsNonApplicableLink, setCallToActionIsNonApplicableLink] = React.useState(false);

    useMessageHandler(
        AdmissionCallToActionOverlayStatusChanged.id,
        (event: AdmissionCallToActionOverlayStatusChanged) => {
            switch (event.status) {
                case 'disabled':
                    setCallToActionIsDisabled(event.newValue);
                    break;
                case 'nonApplicableLink':
                    setCallToActionIsNonApplicableLink(event.newValue);
                    break;
                default:
                    assertNever(event.status);
            }
        }
    );

    return (
        <div className={styles.StudyPageNavigationControls}>
            <div className={styles.ExpandContainer}>
                <Button
                    className={styles.ExpandButton}
                    type={ButtonType.Clear}
                    onClick={onExpandClick}
                    theme={{ withRoundedCorners }}
                    aria-controls={ariaControlsId}
                    aria-expanded={isOpen}
                >
                    <SvgIcon
                        className={classNames(styles.ExpandButtonIcon, isOpen && styles['ExpandButtonIcon--flipped'])}
                        iconName="chevron-right"
                    />
                    <span className={styles.ExpandButtonLabel}>{currentItemLabel}</span>
                </Button>
                <div className={styles.Content}>{children}</div>
            </div>
            {!callToActionIsNonApplicableLink && (
                <Button
                    className={styles.ApplyButton}
                    theme={{ withRoundedCorners }}
                    disabled={callToActionIsDisabled}
                    size={ButtonSize.Medium}
                    tier={ButtonTier.Secondary}
                    type={ButtonType.Solid}
                    onClick={onApplyClick}
                >
                    <span>{applyButtonLabel}</span>
                    <SvgIcon className={styles.ApplyIcon} iconName="arrow-right" />
                </Button>
            )}
        </div>
    );
};
